import React, { useState, useEffect, useRef } from 'react';
import { FileText, Plus, Search, Sparkles, Layers } from 'lucide-react';
import * as mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../Utils/toastContext';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

export async function pdfToTiptap(file) {
  const arrayBuffer = await file.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  let text = '';

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();

    text += content.items.map((item) => item.str).join(' ') + '\n';
  }

  return {
    type: 'doc',
    content: text
      .split('\n')
      .filter(Boolean)
      .map((line) => ({
        type: 'paragraph',
        content: [{ type: 'text', text: line }],
      })),
  };
}
export const mock = [
  {
    id: 'TPL-001',
    name: 'Standard Property Contract',
    status: 'Draft',
    version: 3,
    clauses: [],
  },
  {
    id: 'TPL-002',
    name: 'Employment Agreement',
    status: 'Published',
    version: 2,
    clauses: [],
  },
];

function parseToTipTapNodes(text) {
  const lines = text.split('\n').filter(Boolean);

  const nodes = [];

  for (let line of lines) {
    // 🔲 CHECKBOX
    if (line.includes('☐') || line.includes('[ ]')) {
      nodes.push({
        type: 'paragraph',
        content: [
          {
            type: 'checkbox',
            attrs: {
              checked: false,
              label: line.replace(/☐|\[ \]/g, '').trim(),
              id: crypto.randomUUID(),
            },
          },
        ],
      });
      continue;
    }

    // 🔵 PLACEHOLDER detection
    const placeholderMatch = line.match(/\[([A-Z_]+)\]/);

    if (placeholderMatch) {
      const key = placeholderMatch[1];

      nodes.push({
        type: 'paragraph',
        content: [
          { type: 'text', text: line.split(`[${key}]`)[0] },
          {
            type: 'placeholder',
            attrs: {
              id: crypto.randomUUID(),
              key,
              label: key.toLowerCase(),
              value: '',
            },
          },
          { type: 'text', text: line.split(`[${key}]`)[1] || '' },
        ],
      });

      continue;
    }

    // ✍️ SIGNATURE detection
    if (/sign|signature/i.test(line)) {
      nodes.push({
        type: 'paragraph',
        content: [
          {
            type: 'signature',
            attrs: {
              id: crypto.randomUUID(),
              label: 'Sign',
              value: '',
            },
          },
        ],
      });
      continue;
    }

    // 🧾 NORMAL PARAGRAPH
    nodes.push({
      type: 'paragraph',
      content: [{ type: 'text', text: line }],
    });
  }

  return nodes;
}
function paginate(nodes, limit = 30) {
  const pages = [];
  let current = [];

  nodes.forEach((node) => {
    current.push(node);

    if (current.length >= limit) {
      pages.push(current);
      current = [];
    }
  });

  if (current.length) pages.push(current);

  return pages;
}
const buildTemplateFromFile = async (file) => {
  const isDocx = file.name.toLowerCase().endsWith('.docx');

  let text = '';

  if (isDocx) {
    const arrayBuffer = await file.arrayBuffer(); // ✅ FIX 1

    const result = await mammoth.extractRawText({
      arrayBuffer,
    });

    text = result.value;
  } else {
    text = await file.text();
  }

  // 1. convert text → structured tiptap nodes
  const nodes = parseToTipTapNodes(text);

  // 2. paginate
  const pagesArray = paginate(nodes, 30);

  // 3. build pages
  const pages = pagesArray.map((content, i) => ({
    id: `page-${i + 1}`,
    content: {
      type: 'doc',
      content,
    },
  }));

  return {
    name: file.name,
    pages,
  };
};

function FileUpload({ onLoad, setEditorData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    try {
      setError('');

      const file = e.target.files[0];
      if (!file) return;

      // 🔥 VALIDATION 1: extension check
      const isDocxByExtension = file.name.toLowerCase().endsWith('.docx');

      // 🔥 VALIDATION 2: MIME type check (browser dependent but useful)
      const isDocxByMime =
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (!isDocxByExtension || !isDocxByMime) {
        setError('Only .docx files are allowed');
        e.target.value = '';
        return;
      }

      console.log('File selected:', file.name);

      const template = await buildTemplateFromFile(file);

      console.log('FINAL TEMPLATE:', template);

      navigate('/create-template', {
        state: {
          template,
          from: location.pathname,
          historyStack: [
            ...(location.state?.historyStack || []),
            location.pathname,
          ],
        },
      });
    } catch (err) {
      console.error('File conversion failed:', err);
      setError('Failed to process file. Please try again.');
    }
  };

  return (
    <div className="space-y-2">
      <label className="block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-[#375AE7] transition">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => {
            handleFile(e);
          }}
        />

        <p className="text-sm text-gray-600">Upload file (DOCX only)</p>

        <p className="text-xs text-gray-400 mt-1">
          It will be converted into editable contract format
        </p>
      </label>

      {/* 🔥 ERROR MESSAGE */}
      {error && <p className="text-xs text-red-500 text-center">{error}</p>}
    </div>
  );
}

export default function TemplateDashboard() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null);
  const [showRecords, setShowRecords] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterText, setFilterText] = useState('');

  const PAGE_SIZE = 10;
  const location = useLocation();
  const navigate = useNavigate();
  // null | "create" | "editor"
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
          status: response.status,
        }));

        // Ensure errorData.message is extracted properly
        const errorMessage =
          typeof errorData === 'object' && errorData.message
            ? errorData.message
            : `Error ${response.status}: ${response.statusText}`;

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getAllTemplates = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/contract-library?page=${currentPage}&page_size=${PAGE_SIZE}&search=${filterText}`,
      );

      if (response.status == 200) {
        setTemplates(response.data.contracts);
        setTotalPages(response.data.pagination.total_pages || 1);
        setTotalCount(response.data.pagination.total || 0);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      setTemplates([]);
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    getAllTemplates();
  }, [currentPage, filterText]);
  const fileUploadNew = async (file) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/templates/upload`;
    const formData3 = new FormData();
    formData3.append('file', file); // Append file
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: formData3,
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const handleFile = async (e) => {
    const selectedFile = e.target.files[0];
    let toastMessage = null;

    if (selectedFile) {
      const fileType = selectedFile.type;
      if (['application/pdf'].includes(fileType)) {
        setLoading(true);
        try {
          const response = await fileUploadNew(selectedFile);
          if (response.status == 201) {
            console.log('message' + response.message);
          } else {
            toastMessage = { message: response.message, type: 'error' };
          }
        } catch (err) {
          toastMessage = { message: err.message, type: 'error' };
        } finally {
          setLoading(false); // Ensure `loading` is false first
          // Show toast after loading is false
          if (toastMessage) {
            setTimeout(() => {
              showToast(toastMessage.message, toastMessage.type);
            }, 300); // Small delay if needed
          }
        }
      } else {
        showToast('File must be PDF', 'error');
      }
    }
    e.target.value = '';
  };
  return (
    <div className="h-full lg:min-w-[78vw] lg:max-w-[80vw] rounded-lg shadow-lg bg-gradient-to-br from-[#f5f7ff] via-white to-[#f8faff] text-gray-800 flex flex-col">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {/* TOP HEADER */}
      <div className="px-8 py-6 border-b bg-white/60 rounded-lg shadow-lg backdrop-blur-xl flex items-center justify-between">
        {/* LEFT BRAND */}
        <div className="flex items-center gap-3 ">
          <div className="w-10 h-10 rounded-xl bg-[#375AE7] flex items-center justify-center text-white shadow">
            <Layers size={18} />
          </div>

          <div>
            <h1 className="text-xl font-bold">Document Studio</h1>
            <p className="text-xs text-gray-500">
              Manage and generate legal documents
            </p>
          </div>
        </div>

        {/* RIGHT ACTIONS */}
        <div className="flex items-center gap-3">
          {/* SEARCH */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-4 py-2 w-72">
            <Search size={16} className="text-gray-500" />
            <input
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search documents..."
              className="bg-transparent outline-none text-sm w-full"
            />
          </div>

          {/* CREATE */}
          <button
            onClick={() => setMode('create')}
            className="flex items-center gap-2 bg-[#375AE7] text-white px-4 py-2 rounded-xl"
          >
            <Plus size={16} />
            New Document
          </button>
        </div>
      </div>
      {mode === 'create' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          {/* Modal */}
          <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-semibold text-slate-800">
                Create New Document
              </h2>

              <button
                onClick={() => setMode(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            {/* Options */}
            <div className="grid gap-4">
              {/* Blank Template */}
              <div
                onClick={() => {
                  setMode('editor');
                  navigate('/create-template', {
                    state: {
                      from: location.pathname,
                      historyStack: [
                        ...(location.state?.historyStack || []),
                        location.pathname,
                      ],
                    },
                  });
                }}
                className="group cursor-pointer rounded-2xl border p-4 hover:shadow-lg hover:border-indigo-400 transition"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 text-xl">
                    ✨
                  </div>

                  <div>
                    <div className="font-semibold text-slate-700">
                      Start Blank
                    </div>
                    <div className="text-sm text-slate-500">
                      Create document from scratch
                    </div>
                  </div>
                </div>
              </div>

              {/* Upload Template */}
              <div className="rounded-2xl border p-4 hover:shadow-lg hover:border-indigo-400 transition">
                <FileUpload
                  onLoad={(json) => {
                    setMode('editor');
                  }}
                  // setEditorData={setEditorData}
                />
              </div>

              {/* Upload PDF (NEW) */}
              {/* <label className="group cursor-pointer rounded-2xl border p-4 hover:shadow-lg hover:border-indigo-400 transition">
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    handleFile(e);
                  }}
                />

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-red-100 text-red-600 text-xl">
                    📄
                  </div>

                  <div>
                    <div className="font-semibold text-slate-700">
                      Upload PDF
                    </div>
                    <div className="text-sm text-slate-500">
                      View PDF in read-only mode
                    </div>
                  </div>
                </div>
              </label> */}
            </div>

            {/* Footer */}
            <div className="mt-6 text-center">
              <button
                onClick={() => setMode(null)}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-auto px-8 py-6">
        {/* TITLE ROW */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold">Your Documents</h2>
            <p className="text-sm text-gray-500">Select a document to view</p>
          </div>
        </div>

        {/* TEMPLATE GRID */}
        {templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            {/* ICON */}
            <div className="w-16 h-16 rounded-2xl bg-[#375AE7]/10 flex items-center justify-center mb-4">
              <FileText className="text-[#375AE7]" size={28} />
            </div>

            {/* TITLE */}
            <h2 className="text-lg font-semibold text-gray-800 mb-1">
              No documents available
            </h2>

            {/* SUBTEXT */}
            <p className="text-sm text-gray-500 max-w-sm mb-6">
              You haven’t created or received any documents yet. Start by
              creating a new document or importing one.
            </p>

            {/* CTA BUTTON */}
            <button
              // onClick={() => navigate('/create-template')}
              onClick={() => setMode('create')}
              className="px-5 py-2.5 bg-[#375AE7] text-white rounded-xl text-sm font-medium hover:bg-[#2f4ad1] transition"
            >
              Create New
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {templates.map((t) => (
              <div
                key={t.id}
                onClick={() => {
                  let path = '';
                  if (t.status == 'draft') {
                    path = `/edit-template/${t.id}`;
                  } else {
                    path = `/view-document-details/${t.id}`;
                  }
                  let redirectState = {
                    path: path,
                    state: {
                      from: location.pathname,
                      historyStack: [
                        ...(location.state?.historyStack || []),
                        location.pathname,
                      ],
                    },
                  };
                  sessionStorage.setItem(
                    'redirectAfterLogin',
                    JSON.stringify(redirectState),
                  );
                  navigate(path, {
                    state: {
                      from: location.pathname,
                      historyStack: [
                        ...(location.state?.historyStack || []),
                        location.pathname,
                      ],
                    },
                  });
                }}
                className="group bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition cursor-pointer hover:border-[#375AE7]/30"
              >
                <div className="flex items-center justify-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#375AE7]/10 flex items-center justify-center mb-4 group-hover:bg-[#375AE7]/20 transition">
                    <FileText className="text-[#375AE7]" size={18} />
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[#375AE7] transition whitespace-normal break-words capitalize">
                    {t.title || 'Untitled Contract'}
                  </h3>
                </div>

                <div className="text-xs text-gray-500 flex items-center justify-between mt-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                      t.status?.toLowerCase() === 'published'
                        ? 'bg-green-100 text-green-700'
                        : t.status?.toLowerCase() === 'draft'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {t.status?.charAt(0).toUpperCase() +
                      t.status?.slice(1).toLowerCase()}
                  </span>

                  <span>v{t.version_number}</span>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-gray-400">Click to open</span>
                  <Sparkles
                    size={16}
                    className="text-gray-300 group-hover:text-[#375AE7] transition"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        {/* PAGINATION */}
        {templates.length > 0 && (
          <div className="flex flex-wrap justify-between items-center mt-4 space-y-4 sm:space-y-0">
            {/* Entries Info Section */}
            <div className="text-[#8C8C8C] text-sm sm:text-base w-full sm:w-auto text-center sm:text-left">
              Showing {showRecords * (currentPage - 1) + 1} to{' '}
              {currentPage === totalPages || totalPages == 0
                ? totalCount
                : Math.max(showRecords * currentPage, templates.length)}{' '}
              of {totalCount} entries
            </div>

            {/* Pagination Section */}
            <div className="flex flex-wrap items-center justify-center sm:justify-end font-semibold text-sm gap-2 w-full sm:w-auto">
              {/* Previous Button */}
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>

              {/* Page Buttons */}
              {Array.from({ length: Math.min(totalPages, 5) }).map(
                (_, index) => {
                  const startPage = Math.max(
                    1,
                    Math.min(totalPages - 4, currentPage - 2),
                  );
                  const page = startPage + index;

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded ${
                        currentPage === page
                          ? 'bg-[#375AE7] text-white'
                          : 'bg-white border border-gray-300 text-black'
                      }`}
                    >
                      {page}
                    </button>
                  );
                },
              )}

              {/* Next Button */}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
